/**
 * @component ry-theme-panel
 * @category  display
 * @summary   Floating mode selector (ry-ui-only). Three-button row: auto / light / dark. The auto button uses the OS preference (removes `<html data-ry-mode>` entirely); light/dark force the explicit mode. Persists in localStorage under the shared `ry-theme-panel` key — `<ry-theme-toggle>` reads the same key.
 *
 * @attribute mode   'auto' | 'light' | 'dark' ?   default="auto"   Initial mode if no stored value AND no `<html data-ry-mode>` is present. Resolution order: stored > [mode] attribute > document.documentElement.dataset.ryMode > 'auto'.
 *
 * @property  theme  string   Read-only — always returns `"default"`. The theme system is intentionally minimal at this stage (one opinionated theme). The setter is a no-op.
 * @property  mode   'auto' | 'light' | 'dark'   Live mode. Setting applies + updates UI + persists.
 *
 * @event ry:theme-panel-change
 *        detail: { theme: 'default', mode: 'auto' | 'light' | 'dark' }
 *        fires:  every time the user clicks a mode button. Does NOT fire when setting `el.mode` programmatically (the property setter applies + persists but is silent — to broadcast a programmatic change, dispatch the event yourself or use the button-click path).
 *
 * @state     None on the host. Per-button `aria-pressed="true|false"` reflects the active mode.
 *
 * @content   None — `<ry-theme-panel>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom
 *   ry-theme-panel
 *     > div.ry-theme-panel   [data-ry-target="panel"]
 *       > div.ry-theme-panel__row   [data-ry-target="mode-row"]
 *         > span   [data-ry-target="label"]   "Mode"
 *         > div    [data-ry-target="mode-options"]
 *           > button   [data-ry-target="mode-btn"] data-value="auto"  aria-pressed?
 *           > button   [data-ry-target="mode-btn"] data-value="light" aria-pressed?
 *           > button   [data-ry-target="mode-btn"] data-value="dark"  aria-pressed?
 *
 * @gotcha  `mode="auto"` REMOVES `<html data-ry-mode>` and `color-scheme` instead of setting them. This makes CSS fall back to the OS preference via `prefers-color-scheme`. Setting to light/dark applies the values.
 *
 * @gotcha  Shared storage key `ry-theme-panel` with `<ry-theme-toggle>`. Both components read + write the same JSON shape `{"mode":"..."}`. Toggle and panel stay in sync if both are mounted at once — but `<ry-theme-toggle>` doesn't watch storage changes, so changes made by panel won't immediately update the toggle's icon unless the page reloads. Document this if both are visible together.
 *
 * @gotcha  The `.mode` property SETTER does not fire `ry:theme-panel-change`. Only the button click path does. To programmatically change mode + broadcast, simulate a click on the matching button (or dispatch the event yourself).
 *
 * @gotcha  Setup-once render — there's no observedAttributes. Changing `<ry-theme-panel mode="...">` after mount does NOT re-render the panel or change the active mode. Use `el.mode = "..."` for runtime updates.
 *
 * @example basic
 * ```html
 * <ry-theme-panel></ry-theme-panel>
 * ```
 *
 * @example initial-mode
 * ```html
 * <ry-theme-panel mode="dark"></ry-theme-panel>
 * ```
 *
 * @tutorial mount-renders-three-mode-buttons
 * ```js
 * const el = mount('<ry-theme-panel></ry-theme-panel>');
 * const btns = el.querySelectorAll('[data-ry-target="mode-btn"]');
 * await expectEqual(btns.length, 3);
 * await expectAttr(btns[0], 'data-value', 'auto');
 * await expectAttr(btns[1], 'data-value', 'light');
 * await expectAttr(btns[2], 'data-value', 'dark');
 * ```
 *
 * @tutorial active-button-has-aria-pressed-true
 * ```js
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * document.documentElement.removeAttribute('data-ry-mode');
 * const el = mount('<ry-theme-panel mode="dark"></ry-theme-panel>');
 * const dark = el.querySelector('[data-value="dark"]');
 * const light = el.querySelector('[data-value="light"]');
 * await expectAttr(dark, 'aria-pressed', 'true');
 * await expectAttr(light, 'aria-pressed', 'false');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial click-button-fires-ry-theme-panel-change
 * ```js
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * const el = mount('<ry-theme-panel></ry-theme-panel>');
 * const fired = [];
 * el.addEventListener('ry:theme-panel-change', (e) => fired.push(e.detail));
 * el.querySelector('[data-value="dark"]').click();
 * await expectEqual(el.mode, 'dark');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].mode, 'dark');
 * await expectEqual(fired[0].theme, 'default');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial auto-mode-removes-html-data-ry-mode
 * ```js
 * // Wrong assumption: auto sets data-ry-mode="auto". It REMOVES the attr
 * // so CSS falls back to prefers-color-scheme.
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * const el = mount('<ry-theme-panel></ry-theme-panel>');
 * el.querySelector('[data-value="dark"]').click();
 * await expectAttr(document.documentElement, 'data-ry-mode', 'dark');
 * el.querySelector('[data-value="auto"]').click();
 * await expectEqual(document.documentElement.hasAttribute('data-ry-mode'), false, 'auto removes attr');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial light-and-dark-set-color-scheme
 * ```js
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * const el = mount('<ry-theme-panel></ry-theme-panel>');
 * el.querySelector('[data-value="light"]').click();
 * await expectAttr(document.documentElement, 'data-ry-mode', 'light');
 * await expectEqual(document.documentElement.style.colorScheme, 'light');
 * el.querySelector('[data-value="dark"]').click();
 * await expectEqual(document.documentElement.style.colorScheme, 'dark');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial restores-from-shared-localstorage-key
 * ```js
 * localStorage.setItem('ry-theme-panel', JSON.stringify({ mode: 'dark' }));
 * const el = mount('<ry-theme-panel></ry-theme-panel>');
 * await expectEqual(el.mode, 'dark', 'restored from shared storage');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @tutorial programmatic-setter-is-silent
 * ```js
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * const el = mount('<ry-theme-panel></ry-theme-panel>');
 * const fired = [];
 * el.addEventListener('ry:theme-panel-change', () => fired.push(1));
 * el.mode = 'dark';
 * await expectEqual(el.mode, 'dark', 'setter does change state');
 * await expectEqual(fired.length, 0, 'but setter does NOT fire event');
 * try { localStorage.removeItem('ry-theme-panel'); } catch (e) {}
 * ```
 *
 * @see ry-theme-toggle
 */

import { RyElement } from '../core/ry-element.js';

interface ThemePanelChangeDetail {
  theme: string;
  mode: string;
}

const STORAGE_KEY = 'ry-theme-panel';

export class RyThemePanel extends RyElement {
  #mode = 'auto';

  setup(): void {
    const stored = this.#loadStorage();
    this.#mode = stored?.mode
      ?? this.getAttribute('mode')
      ?? document.documentElement.dataset.ryMode
      ?? 'auto';

    this.innerHTML = this.#render();
    this.#bindButtons();
    this.#apply();
  }

  #bindButtons(): void {
    this.$$<HTMLButtonElement>('[data-ry-target="mode-btn"]').forEach(btn => {
      this.on(btn, 'click', () => {
        this.#mode = btn.dataset.value ?? 'auto';
        this.#apply();
        this.#updatePressed();
        this.#save();
        this.emit<ThemePanelChangeDetail>('theme-panel-change', {
          theme: 'default', mode: this.#mode
        });
      });
    });
  }

  #apply(): void {
    const root = document.documentElement;
    if (this.#mode === 'auto') {
      delete root.dataset.ryMode;
      root.style.removeProperty('color-scheme');
    } else {
      root.dataset.ryMode = this.#mode;
      root.style.colorScheme = this.#mode;
    }
  }

  #updatePressed(): void {
    this.$$('[data-ry-target="mode-btn"]').forEach(btn => {
      const el = btn as HTMLElement;
      el.setAttribute('aria-pressed', String(el.dataset.value === this.#mode));
    });
  }

  #save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ mode: this.#mode }));
    } catch { /* storage unavailable */ }
  }

  #loadStorage(): { mode: string } | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { mode?: string };
      return parsed.mode ? { mode: parsed.mode } : null;
    } catch { return null; }
  }

  #render(): string {
    return `
      <div data-ry-target="panel" class="ry-theme-panel">
        <div data-ry-target="mode-row" class="ry-theme-panel__row">
          <span data-ry-target="label" class="ry-theme-panel__label">Mode</span>
          <div data-ry-target="mode-options" class="ry-theme-panel__options">
            ${this.#btn('auto', 'Auto')}
            ${this.#btn('light', '&#9728;')}
            ${this.#btn('dark', '&#9790;')}
          </div>
        </div>
      </div>`;
  }

  #btn(value: string, label: string): string {
    return `<button data-ry-target="mode-btn" data-value="${value}" class="ry-theme-panel__btn" aria-pressed="${value === this.#mode}">${label}</button>`;
  }

  // Public API
  get theme(): string { return 'default'; }
  set theme(_v: string) { /* no-op — single theme for now */ }

  get mode(): string { return this.#mode; }
  set mode(v: string) {
    this.#mode = v;
    this.#apply();
    this.#updatePressed();
    this.#save();
  }
}

customElements.define('ry-theme-panel', RyThemePanel);
